import { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, fmt, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, remove, clear, open, setOpen, dueNow } = useCart();
  const [client, setClient] = useState({ artist: "", email: "", phone: "", song_link: "", ig_link: "" });
  const [loading, setLoading] = useState(false);
  const hasDeposit = items.some((i) => i.checkout_type === "deposit_approval");

  const checkout = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        service_ids: items.map((i) => i.id),
        client,
        origin_url: window.location.origin,
      });
      clear();
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  const set = (k) => (e) => setClient({ ...client, [k]: e.target.value });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-testid="cart-drawer"
        className="bg-[#0A0A0A] border-white/10 w-full sm:max-w-md p-0 flex flex-col">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <SheetTitle className="font-display text-2xl text-white flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-brand" /> Your Cart
          </SheetTitle>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" aria-label="Close cart">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <p data-testid="cart-empty" className="text-sm text-zinc-500">Your cart is empty. Browse marketing services to get started.</p>
          ) : (
            <div className="space-y-4">
              {items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex justify-between gap-4 border border-white/10 p-4">
                  <div>
                    <p className="text-sm font-medium text-white">{i.name}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {i.checkout_type === "deposit_approval"
                        ? `${fmt(i.deposit_amount)} deposit today · ${fmt(i.price - (i.deposit_amount || 0))} after approval`
                        : `${fmt(i.price)}${i.unit === "monthly" ? "/mo" : ""}`}
                    </p>
                  </div>
                  <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)}
                    className="text-zinc-500 hover:text-red-400 transition-colors" aria-label="Remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
        <div className="border-t border-white/10 pt-4 space-y-2">
          <div className="flex justify-between text-sm text-zinc-400">
            <span>Subtotal</span><span>{fmt(dueNow)}</span>
          </div>
          <div className="flex justify-between text-sm text-zinc-400">
            <span>Service fee</span><span>{fmt(5)}</span>
          </div>
          <div className="flex justify-between text-sm text-zinc-400">
            <span>Tax</span><span className="text-green-400">$0 — No tax</span>
          </div>
          <div className="flex justify-between border-t border-white/10 pt-2">
            <span className="text-sm text-zinc-400">Due today</span>
            <span data-testid="cart-total" className="font-display text-2xl text-white">{fmt(dueNow + 5)}</span>
          </div>
        </div>
              {hasDeposit && (
                <p className="text-xs text-zinc-500">Deposit-based services are reviewed by our team before the balance is requested.</p>
              )}
              <form onSubmit={checkout} className="space-y-3 pt-2">
                <div>
                  <Label className="text-xs text-zinc-400">Artist / Brand *</Label>
                  <Input data-testid="cart-artist-input" required value={client.artist} onChange={set("artist")}
                    className="bg-white/5 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Email *</Label>
                  <Input data-testid="cart-email-input" required type="email" value={client.email} onChange={set("email")}
                    className="bg-white/5 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Phone *</Label>
                  <Input data-testid="cart-phone-input" required value={client.phone} onChange={set("phone")}
                    className="bg-white/5 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Song Link</Label>
                  <Input data-testid="cart-song-input" value={client.song_link} onChange={set("song_link")}
                    className="bg-white/5 border-white/10 mt-1" placeholder="https://" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Instagram Link</Label>
                  <Input data-testid="cart-ig-input" value={client.ig_link} onChange={set("ig_link")}
                    className="bg-white/5 border-white/10 mt-1" placeholder="https://instagram.com/..." />
                </div>
                <button data-testid="cart-checkout-btn" type="submit" disabled={loading}
                  className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-semibold tracking-widest uppercase text-sm py-3.5 transition-colors">
                  {loading ? "Redirecting to checkout…" : "Checkout"}
                </button>
              </form>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
